import * as React from 'react';
import {connect} from 'react-redux';

import {formValueSelector} from 'redux-form';

import {CloseButton} from 'shells/button/close-button/close-button';
import {MinimizeButton} from 'shells/button/minimize-button/minimize-button';
import {Dialog} from 'shells/dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';

import type {NutshellState} from '../../store';
import {getCurrentPageEntityId} from '../../util/get-current-page-entity-id';

import {GetEntity} from '../core/get-entity';

import {
    enlargeComposer,
    closeComposer,
    openComposerWithNewEmail,
    openComposerWithEmailAddress,
    cancelOverrideMinimizedEmail,
} from './email-composer-actions';
import {
    getSelectedSequenceId,
    getShouldShowMinimizedEmailBar,
    getWasMinimizedForEntityId,
    getIsPromptingOverride,
    getActiveEntityId,
    getPendingEmailAddress,
} from './email-composer-selectors';
import {getSequencePrimarySubject} from './sender/get-sequence-primary-subject';
import {FORM_NAME} from './sender/types';

import styles from './minimized-email.module.css';

const valueSelector = formValueSelector(FORM_NAME);

function mapStateToProps(state: NutshellState): StateProps {
    const selectedSequenceId = getSelectedSequenceId(state);
    const sequences = valueSelector(state, 'sequences');

    return {
        isPromptingOverride: getIsPromptingOverride(state),
        isShown: getShouldShowMinimizedEmailBar(state),
        entityId: getWasMinimizedForEntityId(state),
        pendingEntityId: getActiveEntityId(state),
        pendingEmailAddress: getPendingEmailAddress(state),
        subject:
            sequences && sequences[selectedSequenceId]
                ? getSequencePrimarySubject(sequences[selectedSequenceId])
                : '',
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onEnlarge: () => {
            dispatch(enlargeComposer());
        },
        onDiscard: () => {
            dispatch(closeComposer());
        },
        onDiscardAndOpenNew: (entityId?: string, emailAddress?: string) => {
            if (emailAddress && entityId) {
                dispatch(openComposerWithEmailAddress({emailAddress, entityId}));
            } else {
                dispatch(openComposerWithNewEmail());
            }
        },
        onCancelOverride: () => {
            dispatch(cancelOverrideMinimizedEmail());
        },
    };
}

type StateProps = {
    isShown: boolean;
    subject: string;
    entityId: string | null | undefined;
    pendingEntityId: string | null | undefined;
    pendingEmailAddress: string | null | undefined;
    isPromptingOverride: boolean;
};

type DispatchProps = {
    onEnlarge: () => any;
    onDiscard: () => any;
    onDiscardAndOpenNew: (entityId?: string, emailAddress?: string) => any;
    onCancelOverride: () => any;
};

type Props = StateProps & DispatchProps;

type State = {
    isConfirmingClose: boolean;
};

export class MinimizedEmailComponent extends React.Component<Props, State> {
    container: HTMLElement | null | undefined;
    closeButton: HTMLElement | null | undefined;
    currentPageEntityId: string | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isConfirmingClose: false,
        };

        this.currentPageEntityId = getCurrentPageEntityId();
    }

    render() {
        if (!this.props.isShown || !this.props.entityId) {
            return null;
        }

        return (
            <GetEntity entityId={this.props.entityId}>
                {({entity}) => {
                    return (
                        <div
                            ref={(node) => {
                                this.container = node;
                            }}
                            onClick={this.handleEnlargeClick}
                            className={styles['minimized-email']}
                        >
                            <EmailIcon size={21} />
                            <div className={styles.summary}>
                                <div className={styles.subject}>{this.props.subject}</div>
                                <span>
                                    Draft to: <LinkedEntity isLinkable={false} {...entity} />
                                </span>
                            </div>
                            <div className={styles.buttons}>
                                <MinimizeButton tooltip='Enlarge' onClick={this.props.onEnlarge} />
                                <CloseButton tooltip='Discard' onClick={this.handleCloseClick} />
                            </div>
                            {this.state.isConfirmingClose && (
                                <Dialog
                                    anchor={this.container}
                                    location='top'
                                    question='Are you sure you want to discard this email?'
                                    onPrimary={this.handleCloseConfirm}
                                    onSecondary={this.handleCloseCancel}
                                />
                            )}
                            {this.props.isPromptingOverride && (
                                <Dialog
                                    anchor={this.container}
                                    location='top'
                                    question='You’re in the middle of an email, would you like to discard it and start a new one?'
                                    primaryText='Yes, discard my current email'
                                    secondaryText='Not yet'
                                    onPrimary={() =>
                                        this.props.onDiscardAndOpenNew(
                                            this.props.pendingEntityId ?? undefined,
                                            this.props.pendingEmailAddress ?? undefined
                                        )
                                    }
                                    onSecondary={this.props.onCancelOverride}
                                />
                            )}
                        </div>
                    );
                }}
            </GetEntity>
        );
    }

    handleEnlargeClick = () => {
        this.props.onEnlarge();
    };

    handleCloseClick = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();

        this.setState({isConfirmingClose: true});
    };

    handleCloseConfirm = () => {
        this.setState({isConfirmingClose: false});
        this.props.onDiscard();
    };

    handleCloseCancel = () => {
        this.setState({isConfirmingClose: false});
    };
}

// @ts-expect-error
const connector = connect<Props, Record<any, any>, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const MinimizedEmail = connector(MinimizedEmailComponent);
